import { keyArray } from "@solid-primitives/keyed"
import { createComputed, createEffect, createMemo, createSignal } from "solid-js"
import isIdListSame from "../../../../rx/signal/equals/isIdListSame"
import getID from "../../../ldsh/getID"
import { NEUTRAL_PLAYER_COLOR } from "../../../three/PlayerThreeObject/getEntityColor1Material"
import type { Player } from "../../player/Player.type"
import type { PlayerEntity } from "../../player/PlayerEntity.type"
import type { Engine } from "../Engine.type"
import type { PlayerGraphNode } from "./PlayerGraphNode.type"

/**
 * Represents a neutral player with default properties.
 * Used for entities that don't belong to any specific player.
 */
type NeutralPlayer = Pick<Player, 'dname' | 'color1' | 'color2'> & {
  id: -1,
}

/**
 * Creates a reactive list of player graphs that track player entities and their properties.
 * 
 * @remarks
 * This function creates a reactive system that:
 * 1. Tracks all players including a special neutral player
 * 2. Provides reactive access to player entities and visual properties
 * 3. Maintains efficient updates through Solid.js reactivity
 * 
 * @param engine - The game engine instance containing player and entity state
 * @returns A reactive array of player graphs with the following properties:
 *   - `id`: The unique player ID
 *   - `ents`: A getter for the player's entities
 *   - `color1`: Reactive getter for the primary player color
 *   - `color2`: Reactive getter for the secondary player color
 *   - All standard Player properties (name, etc.)
 * 
 * @example
 * ```typescript
 * // Create player graphs
 * const playerGraphs = createPlayerGraphNodeList(engine);
 * 
 * // Access a player's entities reactively
 * createEffect(() => {
 *   const player = playerGraphs().find(p => p.id === 1);
 *   const entities = player?.ents() || [];
 *   // React to entity changes
 * });
 * ```
 */
export default function createPlayerGraphNodeList(engine: Engine) {
  // Neutral player is always included and has ID -1
  const neutralPlayer: NeutralPlayer = {
    id: -1,
    dname: 'Neutral',
    color1: NEUTRAL_PLAYER_COLOR,
    color2: NEUTRAL_PLAYER_COLOR,
  }

  // Create a reactive array of players including the neutral player
  const players = keyArray(
    // Source function - combines neutral player with engine players
    () => [neutralPlayer, ...engine.state.players],
    // Key function - uses player ID for stable identity
    getID,
    // Transform function - creates the reactive graph for each player
    (player) => {
      // Reactive signals for player colors
      const [getColor1, setColor1] = createSignal<Player['color1']>(0)
      const [getColor2, setColor2] = createSignal<Player['color2']>(0)

      // Update color signals when player data changes
      createComputed(() => {
        const p = player()
        setColor1(p.color1)
        setColor2(p.color2)
      })

      // Get all entities belonging to this player
      function getPlayerEntsRaw() {
        const playerId = player().id
        return engine.state.ents.filter(
          (ent): ent is PlayerEntity => ent.player_id === playerId
        )
      }

      // Memoized version of getPlayerEntsRaw with custom equality check
      const getPlayerEnts = createMemo<Array<PlayerEntity>>(
        getPlayerEntsRaw,
        getPlayerEntsRaw(),
        { equals: isIdListSame }
      )

      // Extend the player object with reactive properties
      const pg = player as PlayerGraphNode
      pg.id = player().id
      pg.ents = getPlayerEnts
      pg.color1 = getColor1
      pg.color2 = getColor2
      return pg
    }
  )

  // Ensure the effect is tracked by Solid's reactivity system
  createEffect(players)

  return players
}